import { SessionKeys, sessionService } from '@/utils/localStorageService'

/**
 * АДРЕС ВОЗВРАТА С ОПЛАТЫ — ЗАБРАТЬ И ОЧИСТИТЬ ДО ПЕРВОГО СОБЫТИЯ (2026-09-28, P0 приватности).
 *
 * Шлюз возвращает на `/thank-you?order_id=…&product_id=…&amount=…&email=…&user_id=…`
 * (utils/payments.ts, parseThankYou). Пиксель Меты берёт для PageView и Purchase полный
 * `document.location`, GA — `page_location`, LinkedIn — адрес страницы: почта покупателя и его id в
 * шлюзе уезжали в три рекламные площадки с каждой оплатой. Порядок это усугублял: ThankYou грузится
 * лениво, и PageView из App.tsx стрелял раньше, чем компонент успевал бы что-то сделать с адресом.
 *
 * Поэтому main.tsx зовёт `captureThankYouReturn()` ДО рендера React: параметры запоминаются в этом
 * модуле, а адрес заменяется на голый `/thank-you` через history.replaceState — ни одна площадка его
 * полным уже не увидит, и роутер стартует с чистого. ThankYou читает параметры отсюда
 * (`thankYouSearch()`), а не из адреса.
 *
 * ПЕРЕЗАГРУЗКА. Страница держит подтверждение 1,8 секунды, и на медленном телефоне ленивый чанк грузится
 * дольше; перезагрузка очищенного адреса показала бы «оплата не прошла» человеку, который только что
 * заплатил, — и он заплатил бы второй раз. Поэтому поля ЗАКАЗА (без почты и user_id) кладутся в
 * sessionStorage этой вкладки и читаются, если адрес пришёл пустым; снимаются, когда страница уводит в
 * кабинет (`forgetThankYouReturn`). Повторного Purchase перезагрузка не даст: order_id уже в
 * `paidOrders`, и ThankYou его гасит.
 */

const THANK_YOU_PATH = /^\/thank-you\/?$/

/** Что переживает перезагрузку: только поля заказа. Почта и user_id шлюза — нет, ни в каком виде. */
const RELOADABLE = ['redirect_status', 'order_id', 'product_id', 'amount', 'run_id'] as const

export const isThankYouPath = (pathname: string): boolean => THANK_YOU_PATH.test(pathname)

/** Поля заказа из query — в том же формате строки запроса, чтобы их читал тот же parseThankYou. */
export const reloadableSearch = (search: string): string => {
  const from = new URLSearchParams(search)
  const kept = new URLSearchParams()
  for (const key of RELOADABLE) {
    const value = from.get(key)
    if (value != null) kept.set(key, value)
  }
  const query = kept.toString()
  return query ? `?${query}` : ''
}

/**
 * Адрес страницы для площадки. На /thank-you — без query и без якоря, что бы в адресе ни осталось:
 * второй замок на случай, если replaceState не сработал. На остальных экранах query остаётся — по
 * utm_* и gclid площадки атрибутируют визит рекламе, — а якорь срезается: ни одна площадка его не
 * читает, а нести в нём можно что угодно.
 */
export const cleanPageLocation = (href: string): string => {
  try {
    const url = new URL(href)
    if (isThankYouPath(url.pathname)) return `${url.origin}${url.pathname}`
    url.hash = ''
    return url.toString()
  } catch {
    /* Неразбираемый адрес — без query и якоря: неизвестное не отправляется. */
    return href.split(/[?#]/)[0] ?? ''
  }
}

export type ReturnWindow = {
  location: { pathname: string; search: string }
  history: { state: unknown; replaceState: (data: unknown, unused: string, url?: string | null) => void }
}

export type ReturnSession = {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

let captured: string | null = null

/**
 * Зовётся ОДИН раз, из main.tsx до createRoot. Вне /thank-you ничего не делает. Окно и хранилище —
 * параметрами, чтобы правило проверялось без браузера (utils/thankYouParams.test.ts).
 */
export const captureThankYouReturn = (
  win: ReturnWindow | undefined = typeof window === 'undefined' ? undefined : window,
  session: ReturnSession = sessionService,
): void => {
  if (!win || !isThankYouPath(win.location.pathname)) return
  const search = win.location.search
  if (!search || search === '?') {
    captured = session.getItem(SessionKeys.thankYouReturn)
    return
  }
  captured = search
  session.setItem(SessionKeys.thankYouReturn, reloadableSearch(search))
  try {
    win.history.replaceState(win.history.state, '', win.location.pathname)
  } catch {
    /* Песочница без history — адрес останется, но GA получит чистый page_location (cleanPageLocation). */
  }
}

/** Строка запроса возврата, как её привёз шлюз (или поля заказа после перезагрузки). null — не было. */
export const thankYouSearch = (): string | null => captured

/** Страница уводит в кабинет: возврат обработан, перезагружать больше нечего. */
export const forgetThankYouReturn = (session: ReturnSession = sessionService): void => {
  captured = null
  session.removeItem(SessionKeys.thankYouReturn)
}
